import { Component, OnInit, OnDestroy, ViewEncapsulation, ChangeDetectorRef, ViewChild, ElementRef, NgZone } from '@angular/core';
import { BaseComponent } from 'app/shared/infrastructure';
import { MessengerHistoryService, NotificationService } from 'app/shared/services';
import { debounceTime, fromEvent, Subject, Subscription, takeUntil } from 'rxjs';
import { formatDate } from '@angular/common';
import { environment } from 'environments/environment';

@Component({
    selector: 'messenger-cmp',
    styleUrls: ['./messenger.scss'],
    templateUrl: './messenger.html',
    encapsulation: ViewEncapsulation.None,
})


export class MessengerComponent extends BaseComponent implements OnInit, OnDestroy {

    items: any[] = [];
    skip = 0;
    take = 10;
    isLoading = false;
    hasMore = true;

    private _destroy$ = new Subject<void>();
    private _stopScroll$ = new Subject<void>();
    private _scrollSubscription?: Subscription;

    @ViewChild('scrollContainer') scrollContainerRef!: ElementRef;

    public constructor(private _socket: MessengerHistoryService,
        private _notificationService: NotificationService,
        private _ngZone: NgZone, private _changeDetectorRef: ChangeDetectorRef) {
        super();

        this._socket.onHistory((history: any) => {
            const newItems = history.items ?? history;

            if (!Array.isArray(newItems)) {
                this.showError('Unexpected history format');
                this.isLoading = false;
                return;
            }

            if (this.skip === 0) {
                this.items = newItems;
            } else {
                this.items.push(...newItems);
            }

            this.skip += newItems.length;
            this.hasMore = newItems.length === this.take;

            if (!this.hasMore) {
                this._stopScroll$.next();
                this._stopScroll$.complete();
                this._scrollSubscription?.unsubscribe();
                this._scrollSubscription = undefined;
            }

            this._notificationService.unReadmessagesCount.next(this.items.filter(f => f.unreadCount > 0).length);
            this.isLoading = false;
        });

        this._socket.onNewItem((summary) => {
            const exists = this.items.find((x) => x.otherUserId === summary.otherUserId);
            this.showInformation(`New message received from ${summary.otherUserFullName}.`);
            if (exists) {
                Object.assign(exists, summary);
                this.items = [summary, ...this.items.filter(x => x.otherUserId !== summary.otherUserId)];
            } else {
                this.items.unshift(summary);
            }
            this._notificationService.unReadmessagesCount.next(this.items.filter(f => f.unreadCount > 0).length);
        });
    }


    ngOnInit(): void {
        this._socket.connect().then(() => {
            this.resetAndLoad();
        });
    }

    ngAfterViewInit(): void {
        this._initScrollListener();
    }

    private _initScrollListener(): void {
        this._ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                requestAnimationFrame(() => {
                    this._scrollSubscription = fromEvent(
                        this.scrollContainerRef.nativeElement,
                        'scroll'
                    )
                        .pipe(
                            debounceTime(100),
                            takeUntil(this._stopScroll$),
                            takeUntil(this._destroy$)
                        )
                        .subscribe(() => {
                            this._ngZone.run(() => this._onScroll());
                        });
                });
            }, 0);
        });
    }

    private resetAndLoad(): void {
        this._stopScroll$ = new Subject<void>();
        this.items = [];
        this.skip = 0;
        this.hasMore = true;
        this.loadData(true);

        requestAnimationFrame(() => this._initScrollListener());
    }

    private _onScroll(): void {
        if (this.isLoading || !this.hasMore) return;

        const el = this.scrollContainerRef.nativeElement;
        const threshold = 50;
        const isAtBottom =
            el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;

        if (isAtBottom) {
            this.loadData();
        }
    }

    private loadData(init = false): void {
        if (this.isLoading || !this.hasMore) return;

        this.isLoading = true;

        this._socket.load(this.skip, this.take);
    }

    onselectItem(item: any): void {
        if (item.unreadCount > 0) {
            this.markAsRead(item.threadId, () => {
                item.unreadCount = 0;
            });

            // this._service.whenUserSelected.next(item);
        } else {
            // this._service.whenUserSelected.next(item);
        }
    }

    private markAsRead(threadId: number, callback: Function): void {
        this._socket.markAsRead(threadId).subscribe({
            next: () => callback(),
            error: (error: any) => this.showError(error),
        });
    }

    onToggleDocument(className: string): void {
        const element = document.getElementById('js-chat-contact');
        element?.classList.toggle(className);
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();

        this._stopScroll$.next();
        this._stopScroll$.complete();

        this._scrollSubscription?.unsubscribe();
        this._scrollSubscription = undefined;
    }

    formatTimestamp(dateStr: string | Date): string {
        const date = new Date(dateStr);
        const today = new Date();

        const isToday =
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();

        return isToday
            ? formatDate(date, 'h:mm a', 'en-US')
            : formatDate(date, 'yyyy-MM-dd', 'en-US');
    }

    onViewAllMessages() {
        this.openLinkInNewTab(environment.messengerUrl);
    }
}
